import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export default function HeroSection() {
  const navigate = useNavigate();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  // 스크롤에 따라 텍스트가 위로 서서히 사라짐 (Apple 패럴렉스)
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#050a12',
      }}
    >
      {/* 배경 그라데이션 레이어 */}
      <Box
        component={motion.div}
        style={{ y: bgY }}
        sx={{
          position: 'absolute',
          inset: '-20%',
          background:
            'radial-gradient(ellipse 80% 60% at 50% 60%, rgba(21,101,192,0.35) 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 20% 20%, rgba(0,188,212,0.15) 0%, transparent 60%)',
          zIndex: 0,
        }}
      />

      {/* 글로우 원형 애니메이션 */}
      <Box
        component={motion.div}
        animate={{ scale: [1, 1.08, 1], opacity: [0.12, 0.2, 0.12] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          width: { xs: '300px', md: '600px' },
          height: { xs: '300px', md: '600px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(33,150,243,0.3) 0%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
        }}
      />

      {/* 메인 텍스트 */}
      <Box
        component={motion.div}
        style={{ y: textY, opacity }}
        sx={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          px: { xs: 3, md: 6 },
          maxWidth: '900px',
          width: '100%',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Typography
            sx={{
              fontFamily: 'NotoSansKR',
              fontWeight: 400,
              fontSize: { xs: '11px', md: '13px' },
              color: 'rgba(100,200,255,0.8)',
              letterSpacing: '5px',
              textTransform: 'uppercase',
              mb: 3,
            }}
          >
            Hardware · Software · ESG
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <Typography
            sx={{
              fontFamily: 'NotoSansKR',
              fontWeight: 900,
              fontSize: { xs: '38px', sm: '54px', md: '72px', lg: '84px' },
              color: '#ffffff',
              lineHeight: 1.1,
              letterSpacing: { xs: '-1px', md: '-2px' },
              mb: 3,
            }}
          >
            사람과 환경을
            <br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(90deg, #4fc3f7, #29b6f6, #0288d1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              함께 생각합니다
            </Box>
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
        >
          <Typography
            sx={{
              fontFamily: 'NotoSansKR',
              fontWeight: 300,
              fontSize: { xs: '15px', md: '19px' },
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.8,
              mb: 5,
              maxWidth: '560px',
              mx: 'auto',
            }}
          >
            텀블러 세척기부터 ESG 플랫폼까지,
            <br />
            혁신적인 하드웨어와 소프트웨어로 더 깨끗한 세상을 만듭니다
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/products/tumblerwasher')}
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 700,
                fontSize: '15px',
                px: 4,
                py: 1.6,
                borderRadius: '50px',
                backgroundColor: '#2196f3',
                color: '#ffffff',
                boxShadow: '0 0 30px rgba(33,150,243,0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#42a5f5',
                  boxShadow: '0 0 40px rgba(33,150,243,0.6)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              제품 알아보기
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/services/daclair')}
              sx={{
                fontFamily: 'NotoSansKR',
                fontWeight: 600,
                fontSize: '15px',
                px: 4,
                py: 1.6,
                borderRadius: '50px',
                color: 'rgba(255,255,255,0.85)',
                borderColor: 'rgba(255,255,255,0.25)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.6)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                },
              }}
            >
              서비스 살펴보기
            </Button>
          </Stack>
        </motion.div>
      </Box>

      {/* 스크롤 인디케이터 */}
      <Box
        component={motion.div}
        style={{ opacity }}
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          bottom: '36px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
        }}
        onClick={() => window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })}
      >
        <Typography
          sx={{
            fontFamily: 'NotoSansKR',
            fontSize: '11px',
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: '3px',
            textTransform: 'uppercase',
          }}
        >
          Scroll
        </Typography>
        <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '20px' }} />
      </Box>
    </Box>
  );
}
